/**
 * Centralized API Client
 * Provides consistent API calling functionality across all services
 */

import { apiConfig } from './apiConfig';
import { supabase } from '../lib/supabaseClient';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

class ApiClient {
  private baseURL: string;
  private timeout: number;
  private retryAttempts: number;
  private isRefreshing = false;
  private refreshSubscribers: Array<{
    onSuccess: () => void;
    onError: (error: Error) => void;
  }> = [];

  constructor() {
    const config = apiConfig.getConfig();
    this.baseURL = config.baseURL;
    this.timeout = config.timeout;
    this.retryAttempts = config.retryAttempts;
  }

  private isDebugEnabled(): boolean {
    if (process.env.NODE_ENV !== 'development') return false;
    try {
      return window.localStorage.getItem('debug_api_client') === 'true';
    } catch {
      return false;
    }
  }

  private debugLog(message: string, payload?: unknown) {
    if (!this.isDebugEnabled()) return;
    if (payload === undefined) {
      // eslint-disable-next-line no-console
      console.info(message);
      return;
    }
    // eslint-disable-next-line no-console
    console.info(message, payload);
  }

  /**
   * Get current Supabase Auth token
   */
  private async getAuthToken(): Promise<string | null> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch (error) {
      this.debugLog('Error getting auth token', error);
      return null;
    }
  }

  /**
   * Subscribe to token refresh completion
   */
  private subscribeToRefresh(onSuccess: () => void, onError: (error: Error) => void) {
    this.refreshSubscribers.push({ onSuccess, onError });
  }

  /**
   * Notify all subscribers that refresh is complete
   */
  private onRefreshed() {
    this.refreshSubscribers.forEach(subscriber => subscriber.onSuccess());
    this.refreshSubscribers = [];
  }

  /**
   * Reject all subscribers when token refresh fails
   */
  private onRefreshFailed(error: Error) {
    this.refreshSubscribers.forEach(subscriber => subscriber.onError(error));
    this.refreshSubscribers = [];
  }

  /**
   * Combine multiple abort signals into one
   */
  private combineAbortSignals(signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();
    
    signals.forEach(signal => {
      if (signal.aborted) {
        controller.abort();
      } else {
        signal.addEventListener('abort', () => controller.abort());
      }
    });
    
    return controller.signal;
  }

  /**
   * Make an authenticated API request with automatic retry and error handling
   */
  async request<T = any>(
    endpoint: string,
    options: RequestInit = {},
    signal?: AbortSignal
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const method = (options.method || 'GET').toUpperCase();
    const isReadMethod = ['GET', 'HEAD', 'OPTIONS'].includes(method);
    
    // Get Supabase Auth token
    const token = await this.getAuthToken();
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    this.debugLog(`API ${options.method || 'GET'} ${url}`, {
      headers,
      body: options.body ? (typeof options.body === 'string' ? JSON.parse(options.body) : options.body) : undefined,
      attempt: 1,
      timestamp: new Date().toISOString()
    });

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Combine external signal with internal timeout signal
    const combinedSignal = signal ? this.combineAbortSignals([signal, controller.signal]) : controller.signal;

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        this.debugLog(`API Request Attempt ${attempt}/${this.retryAttempts}`, {
          url,
          method,
          timestamp: new Date().toISOString()
        });

        const response = await fetch(url, {
          ...options,
          headers,
          signal: combinedSignal,
          credentials: 'include' // Include cookies for authentication
        });

        clearTimeout(timeoutId);

        const emoji = response.ok ? 'OK' : 'ERR';
        this.debugLog(`${emoji} API ${options.method || 'GET'} ${url} ${response.status}`, {
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          attempt,
          timestamp: new Date().toISOString()
        });

        // Handle authentication errors
        if (response.status === 401) {
          this.debugLog('Authentication Error (401)', {
            url,
            isRefreshing: this.isRefreshing,
            timestamp: new Date().toISOString()
          });

          // If already refreshing, queue this request
          if (this.isRefreshing) {
            this.debugLog('Token refresh in progress, queuing request');
            return new Promise<T>((resolve, reject) => {
              this.subscribeToRefresh(() => {
                // Retry the request after refresh
                this.request<T>(endpoint, options).then(resolve).catch(reject);
              }, reject);
            });
          }
          
          // Start refresh process
          this.debugLog('Starting token refresh process');
          this.isRefreshing = true;
          
          try {
            // Try to refresh using Supabase Auth
            this.debugLog('Attempting Supabase token refresh', {
              timestamp: new Date().toISOString()
            });

            const { data, error } = await supabase.auth.refreshSession();
            
            this.debugLog('Supabase token refresh response', {
              success: !error,
              hasSession: !!data.session,
              timestamp: new Date().toISOString()
            });
            
            if (!error && data.session) {
              // Refresh successful, notify subscribers and retry original request
              this.debugLog('Token refresh successful, retrying original request');
              this.onRefreshed();
              this.isRefreshing = false;
              return this.request<T>(endpoint, options);
            } else {
              // Refresh failed, redirect to login
              this.debugLog('Token refresh failed, session expired');
              this.isRefreshing = false;
              const refreshFailure = new Error('Session expired. Please log in again.');
              this.onRefreshFailed(refreshFailure);
              throw refreshFailure;
            }
          } catch (refreshError) {
            this.debugLog('Token refresh error', {
              error: refreshError instanceof Error ? refreshError.message : String(refreshError),
              timestamp: new Date().toISOString()
            });
            this.isRefreshing = false;
            const refreshFailure = new Error('Session expired. Please log in again.');
            this.onRefreshFailed(refreshFailure);
            throw refreshFailure;
          }
        }

        // Handle rate limiting
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : 1000;
          
          if (attempt < this.retryAttempts) {
            await this.delay(waitTime);
            continue;
          }
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          this.debugLog('API Error Response', {
            url,
            status: response.status,
            statusText: response.statusText,
            errorData,
            attempt,
            timestamp: new Date().toISOString()
          });
          const error = new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`) as Error & ApiError;
          error.status = response.status;
          if (typeof errorData.code === 'string') {
            error.code = errorData.code;
          }
          error.details = errorData;
          throw error;
        }

        const data = await response.json();
        this.debugLog('API Success Response', {
          url,
          data,
          attempt,
          timestamp: new Date().toISOString()
        });
        return data;

      } catch (error) {
        lastError = error as Error;
        const apiError = error as ApiError;
        const status = typeof apiError?.status === 'number' ? apiError.status : undefined;
        
        this.debugLog('API Request Error', {
          url,
          error: error instanceof Error ? error.message : String(error),
          status,
          method,
          stack: error instanceof Error ? error.stack : undefined,
          attempt,
          timestamp: new Date().toISOString()
        });
        
        // Don't retry on explicit auth/session failures.
        if (error instanceof Error && (
          error.message.includes('Authentication required') ||
          error.message.includes('Session expired')
        )) {
          this.debugLog('Not retrying due to auth/session failure');
          break;
        }

        // Don't retry on abort (timeout)
        if (error instanceof Error && error.name === 'AbortError') {
          this.debugLog('Not retrying due to timeout/abort');
          break;
        }

        // Don't retry most client errors; 429 is handled separately above.
        if (status !== undefined && status >= 400 && status < 500 && status !== 429) {
          this.debugLog('Not retrying due to non-retriable 4xx response');
          break;
        }

        // Avoid duplicate mutations on write methods for server failures.
        if (status !== undefined && status >= 500 && !isReadMethod) {
          this.debugLog('Not retrying write request after 5xx response');
          break;
        }

        // Retry network/transient errors only for read methods.
        if (status === undefined && !isReadMethod) {
          this.debugLog('Not retrying write request after network/unknown error');
          break;
        }

        // Wait before retry (exponential backoff)
        if (attempt < this.retryAttempts) {
          const waitTime = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
          this.debugLog(`Waiting ${waitTime}ms before retry attempt ${attempt + 1}`);
          await this.delay(waitTime);
        }
      }
    }

    throw lastError || new Error('API request failed after all retry attempts');
  }

  /**
   * GET request
   */
  async get<T = any>(endpoint: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, signal);
  }

  /**
   * POST request
   */
  async post<T = any>(endpoint: string, data?: any, signal?: AbortSignal): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    }, signal);
  }

  /**
   * PUT request
   */
  async put<T = any>(endpoint: string, data?: any, signal?: AbortSignal): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    }, signal);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, signal);
  }

  /**
   * PATCH request
   */
  async patch<T = any>(endpoint: string, data?: any, signal?: AbortSignal): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    }, signal);
  }

  /**
   * Delay utility for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if user is authenticated
   * With HttpOnly cookies, we can't check token validity client-side
   * This is handled by the server
   */
  isAuthenticated(): boolean {
    // Authentication is handled by HttpOnly cookies
    // We can't check token validity client-side
    return true;
  }

  /**
   * Get current base URL
   */
  getBaseURL(): string {
    return this.baseURL;
  }
}

export const apiClient = new ApiClient();
